import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import * as XLSX from "xlsx";
import {
  Search,
  FileSpreadsheet,
  FileText,
  ChevronDown,
  TrendingUp,
  AlertCircle,
  Users,
  ChevronRight,
  TrendingUp as TrendingUpIcon,
  Loader2,
} from "lucide-react";
import { DatePicker } from "antd";
import dayjs from "dayjs";
import "antd/dist/reset.css";
import { useTheme } from "../context/ThemeContext";

// ─── Import from your existing api.js ────────────────────────────────────────
// Adjust the path below to wherever your api.js lives
import {
  dashboardApi,
  authHelpers,
  fetchApiDirect,
  leadAPIApi,
  teamsApi,
  leadsApi,
} from "../services/api";

// ─── Extra business/target endpoints not in existing dashboardApi ─────────────
// We use fetchApiDirect (which internally calls fetchApi with full auth + refresh)
const businessApi = {
  // GET /dashboard/business/kpi?type=ORDERS&startDate=…&endDate=…&process=…&shift=…
  getKPI: (type, startDate, endDate, extra = {}) => {
    const p = new URLSearchParams({ type });
    if (startDate) p.append("startDate", startDate);
    if (endDate) p.append("endDate", endDate);
    if (extra.process) p.append("process", extra.process);
    if (extra.shift) p.append("shift", extra.shift);
    if (extra.source) p.append("source", extra.source);
    if (extra.teamId) p.append("teamId", extra.teamId);
    if (extra.teamLeadId) p.append("teamLeadId", extra.teamLeadId);
    return fetchApiDirect(`/dashboard/business/kpi?${p}`);
  },

  // GET /dashboard/business/monthly-performance?type=ORDERS&startDate=…&endDate=…
  getMonthlyPerformance: (type, startDate, endDate, source, extra = {}) => {
    const p = new URLSearchParams({ type });
    if (startDate) p.append("startDate", startDate);
    if (endDate) p.append("endDate", endDate);
    if (source) p.append("source", source);
    if (extra.teamId) p.append("teamId", extra.teamId);
    if (extra.teamLeadId) p.append("teamLeadId", extra.teamLeadId);
    return fetchApiDirect(`/dashboard/business/monthly-performance?${p}`);
  },

  // GET /dashboard/business/funnel?type=ORDERS&startDate=…&endDate=…
  getFunnel: (type, startDate, endDate, source, extra = {}) => {
    const p = new URLSearchParams({ type });
    if (startDate) p.append("startDate", startDate);
    if (endDate) p.append("endDate", endDate);
    if (source) p.append("source", source);
    if (extra.teamId) p.append("teamId", extra.teamId);
    if (extra.teamLeadId) p.append("teamLeadId", extra.teamLeadId);
    return fetchApiDirect(`/dashboard/business/funnel?${p}`);
  },

  // GET /dashboard/managers/focus?type=ORDERS&startDate=…&endDate=…
  getManagersFocus: (type, startDate, endDate, source, extra = {}) => {
    const p = new URLSearchParams({ type });
    if (startDate) p.append("startDate", startDate);
    if (endDate) p.append("endDate", endDate);
    if (source) p.append("source", source);
    if (extra.teamId) p.append("teamId", extra.teamId);
    if (extra.teamLeadId) p.append("teamLeadId", extra.teamLeadId);
    return fetchApiDirect(`/dashboard/managers/focus?${p}`);
  },

  // GET /dashboard/managers/all?type=ORDERS&page=0&size=20
  getAllManagers: (type, startDate, endDate, page = 0, size = 20) => {
    const p = new URLSearchParams({ type, page, size });
    if (startDate) p.append("startDate", startDate);
    if (endDate) p.append("endDate", endDate);
    return fetchApiDirect(`/dashboard/managers/all?${p}`);
  },

  // GET /dashboard/managers/{id}/team-insights?type=ORDERS&startDate=…&endDate=…
  getTeamInsights: (managerId, type, startDate, endDate) => {
    const p = new URLSearchParams({ type });
    if (startDate) p.append("startDate", startDate);
    if (endDate) p.append("endDate", endDate);
    return fetchApiDirect(
      `/dashboard/managers/${managerId}/team-insights?${p}`,
    );
  },

  // GET /dashboard/teamleads/{id}/agents?type=ORDERS&page=0&size=10
  getTeamLeadAgents: (tlId, type, startDate, endDate, page = 0, size = 10) => {
    const p = new URLSearchParams({ type, page, size });
    if (startDate) p.append("startDate", startDate);
    if (endDate) p.append("endDate", endDate);
    return fetchApiDirect(`/dashboard/teamleads/${tlId}/agents?${p}`);
  },

  // GET /targets/assignees?targetType=ORDERS&assigneeType=TEAM_LEAD
  getAssignees: (targetType, assigneeType) =>
    fetchApiDirect(
      `/targets/assignees?targetType=${targetType}&assigneeType=${assigneeType}`,
    ),

  // POST /targets/assign
  assignTarget: (body) =>
    fetchApiDirect(`/targets/assign`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    }),
};

// ─── Format helpers ───────────────────────────────────────────────────────────
const fmtN = (v) => {
  if (v == null) return "—";
  const n = Number(v);
  if (isNaN(n)) return String(v);
  return n.toLocaleString("en-IN");
};
const fmtPct = (v) => (v != null ? `${Number(v).toFixed(1)}%` : "—");
const fmtRs = (v) => (v != null ? `₹${fmtN(v)}` : "—");
const fmtTime = (s) => {
  if (s == null || isNaN(Number(s)) || Number(s) <= 0) return "—";
  const sec = Math.round(Number(s));
  const m = Math.floor(sec / 60);
  return m > 0 ? `${m}m ${sec % 60}s` : `${sec}s`;
};

const { RangePicker } = DatePicker;

// ─────────────────────────────────────────────────────────────────────────────
const BusinessDashboard = () => {
  const { currentTheme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();

  // ── state ──────────────────────────────────────────────────────────────────
  const [dates, setDates] = useState([
    dayjs().startOf("month"),
    dayjs().endOf("month"),
  ]);
  const [activeTab, setActiveTab] = useState("ORDERS");
  const [processFilter, setProcessFilter] = useState("");
  const [shiftFilter, setShiftFilter] = useState("");
  const [sourceFilter, setSourceFilter] = useState("");
  const [teamFilter, setTeamFilter] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [teamOptions, setTeamOptions] = useState([{ v: "", l: "All teams" }]);
  const [sourceOptions, setSourceOptions] = useState([
    { v: "",             l: "All sources"            },
    { v: "FACEBOOK",     l: "Facebook"               },
    { v: "INSTAGRAM",    l: "Instagram"              },
    { v: "GOOGLE",       l: "Google"                 },
    { v: "MANUAL",       l: "Manual"                 },
    { v: "WHATSAPP",     l: "WhatsApp"               },
    { v: "F_LEAD",       l: "F-Lead"                 },
    { v: "RENEWAL",      l: "Renewal"                },
    { v: "ONBOARDING",   l: "Onboarding"             },
    { v: "REFERRAL",     l: "Referral"               },
    { v: "WEBSITE",      l: "Website"                },
    { v: "F-Lead",       l: "F-Lead (Campaign)"      },
    { v: "Piles",        l: "Piles (Campaign)"       },
    { v: "OnBoarding",   l: "OnBoarding (Campaign)"  },
    { v: "Renewal-Lead", l: "Renewal-Lead (Campaign)"},
    { v: "RTO-Lead",     l: "RTO-Lead (Campaign)"    },
  ]);

  const [kpi, setKpi] = useState(null);
  const [monthly, setMonthly] = useState(null);
  const [funnel, setFunnel] = useState(null);
  const [managers, setManagers] = useState([]);
  const [teamInsights, setTeamInsights] = useState(null);
  const [selectedMgr, setSelectedMgr] = useState(null);
  const [expandedLead, setExpandedLead] = useState(null);
  const [agentsByLead, setAgentsByLead] = useState({});

  const [loading, setLoading] = useState({
    kpi: false,
    monthly: false,
    funnel: false,
    managers: false,
    insights: false,
  });
  const [errors, setErrors] = useState({});

  const [isAssignModalOpen, setIsAssignModalOpen] = useState(false);
  const [assignment, setAssignment] = useState({
    type: "TEAM_LEAD",
    id: "",
    target: "",
  });
  const [assignees, setAssignees] = useState([]);
  const [assigning, setAssigning] = useState(false);

  const [toast, setToast] = useState({
    show: false,
    message: "",
    isError: false,
  });

  // ── helpers ────────────────────────────────────────────────────────────────
  const startDate = dates?.[0]?.format("YYYY-MM-DD");
  const endDate = dates?.[1]?.format("YYYY-MM-DD");

  const showToast = (message, isError = false) => {
    setToast({ show: true, message, isError });
    setTimeout(
      () => setToast({ show: false, message: "", isError: false }),
      3000,
    );
  };
  const setLoad = (k, v) => setLoading((p) => ({ ...p, [k]: v }));
  const setErr = (k, v) => setErrors((p) => ({ ...p, [k]: v }));

  // ── fetchers ───────────────────────────────────────────────────────────────
  const loadKPI = useCallback(async () => {
    setLoad("kpi", true);
    setErr("kpi", null);
    try {
      // 1. Fetch Business KPI
      const kpiPromise = businessApi.getKPI(activeTab, startDate, endDate, {
        process: processFilter,
        shift: shiftFilter,
        source: sourceFilter,
        teamId: teamFilter,
      });

      // 2. Fetch accurate lead counts for the selected source & date range
      const leadParams = new URLSearchParams();
      if (startDate) leadParams.append("startDate", startDate);
      if (endDate)   leadParams.append("endDate", endDate);
      if (sourceFilter) leadParams.append("source", sourceFilter);
      if (teamFilter)   leadParams.append("teamId", teamFilter);
      leadParams.append("page", "0");
      leadParams.append("size", "1");

      const leadsPromise = leadsApi.getAllLeads(`?${leadParams.toString()}`).catch(() => null);

      const [kpiData, leadsData] = await Promise.all([kpiPromise, leadsPromise]);

      const directLeadsCount = Number(
        leadsData?.totalCount ??
        leadsData?.totalElements ??
        leadsData?.totalEntryCount ??
        leadsData?.assignedCount ??
        0
      );

      const resolvedKpiLeads = Number(
        kpiData?.totalAssignedLeads ??
        kpiData?.totalLeads ??
        kpiData?.assignedLeads ??
        kpiData?.leads ??
        0
      );

      const finalLeads = resolvedKpiLeads > 0 ? resolvedKpiLeads : directLeadsCount;

      setKpi({
        ...kpiData,
        totalAssignedLeads: finalLeads,
      });
    } catch (e) {
      setErr("kpi", e.message);
    } finally {
      setLoad("kpi", false);
    }
  }, [
    activeTab,
    startDate,
    endDate,
    processFilter,
    shiftFilter,
    sourceFilter,
    teamFilter,
  ]);

  const loadMonthly = useCallback(async () => {
    setLoad("monthly", true);
    setErr("monthly", null);
    try {
      setMonthly(
        await businessApi.getMonthlyPerformance(
          activeTab,
          startDate,
          endDate,
          sourceFilter,
          { teamId: teamFilter },
        ),
      );
    } catch (e) {
      setErr("monthly", e.message);
    } finally {
      setLoad("monthly", false);
    }
  }, [activeTab, startDate, endDate, sourceFilter, teamFilter]);

  const loadFunnel = useCallback(async () => {
    setLoad("funnel", true);
    setErr("funnel", null);
    try {
      setFunnel(
        await businessApi.getFunnel(
          activeTab,
          startDate,
          endDate,
          sourceFilter,
          { teamId: teamFilter },
        ),
      );
    } catch (e) {
      setErr("funnel", e.message);
    } finally {
      setLoad("funnel", false);
    }
  }, [activeTab, startDate, endDate, sourceFilter, teamFilter]);

  const loadManagers = useCallback(async () => {
    setLoad("managers", true);
    setErr("managers", null);
    try {
      const data = await businessApi.getManagersFocus(
        activeTab,
        startDate,
        endDate,
        sourceFilter,
        { teamId: teamFilter },
      );
      setManagers(
        Array.isArray(data) ? data : data?.managers || data?.data || [],
      );
    } catch (e) {
      setErr("managers", e.message);
    } finally {
      setLoad("managers", false);
    }
  }, [activeTab, startDate, endDate, sourceFilter, teamFilter]);

  const loadTeamInsights = useCallback(
    async (managerId) => {
      setLoad("insights", true);
      setErr("insights", null);
      try {
        const data = await businessApi.getTeamInsights(
          managerId,
          activeTab,
          startDate,
          endDate,
        );
        setTeamInsights(data);
        setSelectedMgr(managerId);
      } catch (e) {
        setErr("insights", e.message);
      } finally {
        setLoad("insights", false);
      }
    },
    [activeTab, startDate, endDate],
  );

  const loadAgents = useCallback(
    async (tlId) => {
      if (agentsByLead[tlId]) return;
      try {
        const data = await businessApi.getTeamLeadAgents(
          tlId,
          activeTab,
          startDate,
          endDate,
        );
        const agents = Array.isArray(data)
          ? data
          : data?.agents || data?.data || [];
        setAgentsByLead((p) => ({ ...p, [tlId]: agents }));
      } catch (e) {
        showToast(`Agents load failed: ${e.message}`, true);
      }
    },
    [agentsByLead, activeTab, startDate, endDate],
  );

  const loadAssignees = useCallback(async () => {
    try {
      const data = await businessApi.getAssignees(activeTab, assignment.type);
      setAssignees(Array.isArray(data) ? data : data?.assignees || []);
    } catch (e) {
      showToast(`Assignees load failed: ${e.message}`, true);
    }
  }, [activeTab, assignment.type]);

  // ── effects ────────────────────────────────────────────────────────────────
  useEffect(() => {
    loadKPI();
    loadMonthly();
    loadFunnel();
    loadManagers();
    setTeamInsights(null);
    setSelectedMgr(null);
    setExpandedLead(null);
    setAgentsByLead({});
  }, [
    activeTab,
    startDate,
    endDate,
    processFilter,
    shiftFilter,
    sourceFilter,
    teamFilter,
  ]);

  useEffect(() => {
    if (isAssignModalOpen) loadAssignees();
  }, [isAssignModalOpen, assignment.type]);

  // ── Fetch complete CRM lead sources, campaigns & teams dynamically ────────
  useEffect(() => {
    const fetchDropdownData = async () => {
      try {
        const [leadRes, campRes, teamsRes] = await Promise.allSettled([
          leadAPIApi.getAllLeadAPIs(),
          leadAPIApi.getAllCampaigns(),
          teamsApi.getAllTeams(),
        ]);

        // 1. Teams
        const teamsRaw = teamsRes.status === "fulfilled" ? teamsRes.value : [];
        const teamsArr = Array.isArray(teamsRaw)
          ? teamsRaw
          : teamsRaw?.content || teamsRaw?.teams || teamsRaw?.data || [];
        const formattedTeams = teamsArr
          .map((t) => ({
            v: String(t.id || t.teamId || ""),
            l: t.teamName || t.name || `Team ${t.id}`,
          }))
          .filter((t) => t.v && t.l);

        setTeamOptions([{ v: "", l: "All teams" }, ...formattedTeams]);

        // 2. Lead Sources & Campaigns
        const leadRaw = leadRes.status === "fulfilled" ? leadRes.value : [];
        const leadArr = Array.isArray(leadRaw)
          ? leadRaw
          : leadRaw?.content || leadRaw?.data || [];

        const campRaw = campRes.status === "fulfilled" ? campRes.value : [];
        const campArr = Array.isArray(campRaw)
          ? campRaw
          : campRaw?.content || campRaw?.data || [];

        const map = new Map();

        const defaultList = [
          { v: "FACEBOOK",     l: "Facebook"               },
          { v: "INSTAGRAM",    l: "Instagram"              },
          { v: "GOOGLE",       l: "Google"                 },
          { v: "MANUAL",       l: "Manual"                 },
          { v: "WHATSAPP",     l: "WhatsApp"               },
          { v: "F_LEAD",       l: "F-Lead"                 },
          { v: "RENEWAL",      l: "Renewal"                },
          { v: "ONBOARDING",   l: "Onboarding"             },
          { v: "REFERRAL",     l: "Referral"               },
          { v: "WEBSITE",      l: "Website"                },
          { v: "F-Lead",       l: "F-Lead (Campaign)"      },
          { v: "Piles",        l: "Piles (Campaign)"       },
          { v: "OnBoarding",   l: "OnBoarding (Campaign)"  },
          { v: "Renewal-Lead", l: "Renewal-Lead (Campaign)"},
          { v: "RTO-Lead",     l: "RTO-Lead (Campaign)"    },
        ];
        defaultList.forEach((opt) => map.set(opt.v.trim().toLowerCase(), opt));

        // Add from Lead API CRM Settings
        leadArr.forEach((item) => {
          const src = item.leadSource || item.sourceName || item.name;
          if (src && src.trim()) {
            const key = src.trim().toLowerCase();
            if (!map.has(key)) {
              map.set(key, {
                v: src.trim(),
                l: item.leadSourceType
                  ? `${src.trim()} (${item.leadSourceType})`
                  : src.trim(),
              });
            }
          }
        });

        // Add from Campaigns
        campArr.forEach((c) => {
          const name = c.campaignName || c.name;
          if (name && name.trim()) {
            const key = name.trim().toLowerCase();
            if (!map.has(key)) {
              map.set(key, {
                v: name.trim(),
                l: `${name.trim()} (Campaign)`,
              });
            }
          }
        });

        setSourceOptions([
          { v: "", l: "All sources" },
          ...Array.from(map.values()),
        ]);
      } catch (e) {
        console.error("Failed to load filter dropdown data:", e);
      }
    };

    fetchDropdownData();
  }, []);

  // ── actions ────────────────────────────────────────────────────────────────
  const handleViewAllManagers = async () => {
    try {
      const data = await businessApi.getAllManagers(
        activeTab,
        startDate,
        endDate,
      );
      setManagers(
        Array.isArray(data) ? data : data?.managers || data?.content || [],
      );
    } catch (e) {
      showToast(`Failed: ${e.message}`, true);
    }
  };

  const handleExportExcel = async () => {
    showToast("Generating export…");
    try {
      // Uses existing dashboardApi.exportDashboardData from your api.js
      await dashboardApi.exportDashboardData({
        dashboardType: activeTab,
        startDate,
        endDate,
      });
      showToast("Excel downloaded!");
    } catch {
      // Fallback — build from current KPI state
      const rows = kpi
        ? [
            { Metric: "Total Assigned Leads", Value: kpi.totalAssignedLeads },
            {
              Metric: "Orders Placed",
              Value: kpi.ordersPlaced ?? kpi.orders ?? "—",
            },
            { Metric: "Verified Orders", Value: kpi.verifiedOrders ?? "—" },
            { Metric: "Avg Order Value", Value: kpi.avgOrderValue ?? "—" },
            { Metric: "Delivered Orders", Value: kpi.deliveredOrders ?? "—" },
            { Metric: "Gross Conversion %", Value: fmtPct(grossConvPct) },
            { Metric: "Verified Conversion %", Value: fmtPct(verifiedConvPct) },
            { Metric: "Net Conversion %", Value: fmtPct(netConvPct) },
            { Metric: "Delivery Rate %", Value: fmtPct(deliveryPct) },
          ]
        : [{ Metric: "No data", Value: "" }];
      const ws = XLSX.utils.json_to_sheet(rows);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Dashboard");
      XLSX.writeFile(wb, `business_dashboard_${activeTab}_${startDate}.xlsx`);
      showToast("Excel downloaded (local data).");
    }
  };

  const toggleLeadExpansion = (tlId) => {
    if (expandedLead === tlId) {
      setExpandedLead(null);
      return;
    }
    setExpandedLead(tlId);
    loadAgents(tlId);
  };

  const submitAssignment = async () => {
    if (!assignment.id || !assignment.target) {
      showToast("Please select a member and enter a target.", true);
      return;
    }
    setAssigning(true);
    try {
      const user = authHelpers.getCurrentUser();
      await businessApi.assignTarget({
        targetType: activeTab,
        assigneeType: assignment.type,
        targetValue: Number(assignment.target),
        startDate,
        endDate,
        assigneeIds: [Number(assignment.id)],
        createdById: user?.id ?? 99,
      });
      showToast("Target assigned successfully!");
      setIsAssignModalOpen(false);
      setAssignment({ type: "TEAM_LEAD", id: "", target: "" });
      loadManagers();
      if (selectedMgr) loadTeamInsights(selectedMgr);
    } catch (e) {
      showToast(`Assignment failed: ${e.message}`, true);
    } finally {
      setAssigning(false);
    }
  };

  // ── derived ────────────────────────────────────────────────────────────────
  const totalLeads = Number(kpi?.totalAssignedLeads ?? kpi?.totalLeads ?? kpi?.assignedLeads ?? kpi?.leads ?? 0);
  const ordersPlaced = Number(kpi?.ordersPlaced ?? kpi?.orders ?? 0);
  const verifiedOrders = Number(kpi?.verifiedOrders ?? kpi?.verified ?? 0);
  const deliveredOrders = Number(kpi?.deliveredOrders ?? kpi?.delivered ?? 0);

  const achieved = monthly?.achieved ?? ordersPlaced;
  const target = monthly?.target ?? kpi?.target ?? kpi?.monthlyTarget ?? 0;
  const pct = target > 0 ? Math.round((achieved / target) * 100) : 0;
  const gap = target - achieved;

  // Gross conversion: backend raw value OR calculate from (ordersPlaced / totalLeads)
  const grossConvRaw = kpi?.grossConversionRate ?? kpi?.grossConversion ?? kpi?.grossConversionPct ?? kpi?.conversionRate;
  const grossConvPct = grossConvRaw != null && !isNaN(Number(grossConvRaw)) && Number(grossConvRaw) > 0
    ? Number(grossConvRaw)
    : (totalLeads > 0 ? (ordersPlaced / totalLeads) * 100 : (ordersPlaced > 0 ? (ordersPlaced / ordersPlaced) * 100 : 0));

  // Verified conversion: backend raw value OR calculate from (verifiedOrders / totalLeads)
  const verifiedConvRaw = kpi?.verifiedConversionRate ?? kpi?.verifiedConversion ?? kpi?.verifiedConversionPct;
  const verifiedConvPct = verifiedConvRaw != null && !isNaN(Number(verifiedConvRaw)) && Number(verifiedConvRaw) > 0
    ? Number(verifiedConvRaw)
    : (totalLeads > 0 ? (verifiedOrders / totalLeads) * 100 : 0);

  // Net conversion: backend raw value OR calculate from (deliveredOrders / totalLeads)
  const netConvRaw = kpi?.netConversionRate ?? kpi?.netConversion ?? kpi?.netConversionPct;
  const netConvPct = netConvRaw != null && !isNaN(Number(netConvRaw)) && Number(netConvRaw) > 0
    ? Number(netConvRaw)
    : (totalLeads > 0 ? (deliveredOrders / totalLeads) * 100 : (ordersPlaced > 0 ? (deliveredOrders / ordersPlaced) * 100 : 0));

  // Delivery %: backend raw value OR calculate from (deliveredOrders / ordersPlaced)
  const deliveryRaw = kpi?.deliveryRate ?? kpi?.deliveryRatePct ?? kpi?.deliveredOrdersPct;
  const deliveryPct = deliveryRaw != null && !isNaN(Number(deliveryRaw))
    ? Number(deliveryRaw)
    : (ordersPlaced > 0 ? (deliveredOrders / ordersPlaced) * 100 : 0);

  // Verification rate %: backend raw value OR calculate from (verifiedOrders / ordersPlaced)
  const verificationRaw = kpi?.verificationRate ?? kpi?.verificationRatePct;
  const verificationPct = verificationRaw != null && !isNaN(Number(verificationRaw))
    ? Number(verificationRaw)
    : (ordersPlaced > 0 ? (verifiedOrders / ordersPlaced) * 100 : 0);

  // Avg AHT
  const avgAHT = kpi?.avgAHT ?? kpi?.aht ?? kpi?.averageHandlingTime ?? kpi?.avgHandlingTime;

  // Funnel steps: fallback to KPI orders/verified/delivered if funnel API is 0 or empty
  const fLeads = Number(funnel?.totalLeads ?? funnel?.leads ?? funnel?.totalAssignedLeads ?? (totalLeads > 0 ? totalLeads : ordersPlaced));
  const fOrders = Number(funnel?.orders ?? funnel?.ordersPlaced ?? ordersPlaced);
  const fVerified = Number(funnel?.verified ?? funnel?.verifiedOrders ?? verifiedOrders);
  const fDelivered = Number(funnel?.delivered ?? funnel?.deliveredOrders ?? deliveredOrders);

  const funnelSteps = (fLeads > 0 || fOrders > 0 || fVerified > 0 || fDelivered > 0)
    ? [
        { label: "Leads",     value: fLeads,     color: `${currentTheme.color}60` },
        { label: "Orders",    value: fOrders,    color: `${currentTheme.color}80` },
        { label: "Verified",  value: fVerified,  color: `${currentTheme.color}A0` },
        { label: "Delivered", value: fDelivered, color: currentTheme.color },
      ]
    : [];

  const statusConfig = (p) => {
    if (p >= 90)
      return {
        label: "Good",
        color: "#22c55e",
        bg: "rgba(34, 197, 94, 0.1)",
        border: "rgba(34, 197, 94, 0.2)",
      };
    if (p >= 75)
      return {
        label: "On track",
        color: "#3b82f6",
        bg: "rgba(59, 130, 246, 0.1)",
        border: "rgba(59, 130, 246, 0.2)",
      };
    return {
      label: "Low performance",
      color: "#f97316",
      bg: "rgba(249, 115, 22, 0.1)",
      border: "rgba(249, 115, 22, 0.2)",
    };
  };

  const tabClass = (tab) =>
    `px-4 py-1.5 rounded-lg text-xs font-bold transition-all active:scale-95 ${
      activeTab === tab
        ? "text-white shadow-md"
        : isDark
          ? "text-gray-300 hover:text-white hover:bg-white/10"
          : "text-gray-600 hover:text-gray-800 hover:bg-gray-100/50"
    }`;

  const tabStyle = (tab) =>
    activeTab === tab ? { backgroundColor: currentTheme.color } : {};

  const Spinner = () => (
    <Loader2
      className="w-4 h-4 animate-spin inline-block mr-1"
      style={{ color: currentTheme.color }}
    />
  );
  const ErrBanner = ({ msg }) => (
    <div className="text-xs text-red-500 bg-red-50 border border-red-100 rounded-lg px-3 py-2 mb-2">
      {msg}
    </div>
  );

  const isDark = currentTheme.textColor === "#FFFFFF";

  // ── JSX ────────────────────────────────────────────────────────────────────
  return (
    <div
      className={`flex flex-col gap-6 p-4 md:p-6 min-h-screen transition-colors duration-300 ${!isDark ? "bg-[#F8FAFC]" : ""}`}
      style={isDark ? { background: currentTheme.cssGradient } : {}}
    >
      {/* ── Header ── */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <h1 className="text-xl font-bold text-gray-800">Business Dashboard</h1>
        <div className="flex items-center gap-4 flex-wrap">
          <div
            className={`custom-range-picker rounded-lg overflow-hidden border ${isDark ? "border-white/10 bg-black/20" : "border-gray-200 bg-white"}`}
          >
            <RangePicker
              value={dates}
              onChange={(vals) => {
                setDates(vals);
                setAgentsByLead({});
              }}
              format="MMM DD, YYYY"
              allowClear={false}
              suffixIcon={
                <ChevronDown
                  className={`w-4 h-4 ${isDark ? "text-gray-300" : "text-gray-400"}`}
                />
              }
              style={{
                borderRadius: "8px",
                border: "none",
                fontWeight: "bold",
                background: "transparent",
              }}
              className={isDark ? "dark-range-picker" : ""}
            />
          </div>
          <div
            className={`flex items-center p-1 rounded-xl gap-1 ${isDark ? "bg-black/40" : "bg-[#F1F5F9]"}`}
          >
            {[
              {
                tab: "ORDERS",
                label: "Orders target",
                path: "/mis-reports/business-dashboard",
              },
              {
                tab: "REVENUE",
                label: "Revenue target",
                path: "/mis-reports/revenue-target",
              },
              {
                tab: "CONVERSION",
                label: "Conversion target",
                path: "/mis-reports/conversion-target",
              },
            ].map(({ tab, label, path }) => (
              <button
                key={tab}
                className={tabClass(tab)}
                style={tabStyle(tab)}
                onClick={() => {
                  setActiveTab(tab);
                  navigate(path);
                }}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* ── Filters ── */}
      <div
        className={`flex flex-wrap items-center justify-between gap-4 p-4 rounded-xl shadow-sm border ${isDark ? "bg-black/20 border-white/10" : "bg-white border-gray-100"}`}
      >
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative group">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 group-focus-within:text-blue-500 transition-colors" />
            <input
              type="text"
              placeholder="Search employee ID, name or code…"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className={`pl-10 pr-4 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 w-64 transition-all ${isDark ? "bg-black/20 border-white/10 text-white placeholder-gray-400" : "bg-gray-50 border-gray-200"}`}
              style={{
                ringColor: currentTheme.color + "20",
                borderColor: currentTheme.color + "40",
              }}
            />
          </div>

          {[
            // {
            //   label: 'Process', value: processFilter, setter: setProcessFilter,
            //   options: [{ v: '', l: 'All processes' }, { v: 'FLead', l: 'FLead' }, { v: 'RTO', l: 'RTO' }],
            // },
            // {
            //   label: "Shift",
            //   value: shiftFilter,
            //   setter: setShiftFilter,
            //   options: [
            //     { v: "", l: "All shifts" },
            //     { v: "MORNING", l: "Morning" },
            //     { v: "EVENING", l: "Evening" },
            //     { v: "NIGHT", l: "Night" },
            //   ],
            // },
            // ✅ Team filter
            {
              label: "Team",
              value: teamFilter,
              setter: setTeamFilter,
              options: teamOptions,
            },
            // ✅ Lead Source filter
            {
              label: "Lead Source",
              value: sourceFilter,
              setter: setSourceFilter,
              options: sourceOptions,
            },
          ].map(({ label, value, setter, options }) => (
            <div key={label} className="flex flex-col">
              <span className="text-[10px] font-bold text-gray-400 uppercase ml-1 mb-0.5">
                {label}
              </span>
              <div className="relative">
                <select
                  value={value}
                  onChange={(e) => setter(e.target.value)}
                  className={`appearance-none border rounded-lg px-3 py-2 pr-8 text-sm font-semibold min-w-[140px] focus:outline-none focus:ring-2 cursor-pointer ${isDark ? "bg-black/20 border-white/10 text-white hover:bg-white/10 [&>option]:bg-[#12141a]" : "bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100"}`}
                  style={{ ringColor: currentTheme.color + "20" }}
                >
                  {options.map((o) => (
                    <option key={o.v} value={o.v}>
                      {o.l}
                    </option>
                  ))}
                </select>
                <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
              </div>
            </div>
          ))}
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={handleExportExcel}
            className="flex items-center gap-2 px-4 py-2 text-white rounded-lg text-sm font-bold transition-all"
            style={{ backgroundColor: currentTheme.color }}
          >
            <FileSpreadsheet className="w-4 h-4" /> Export Excel
          </button>
          <button
            onClick={() => {
              showToast("Generating PDF…");
              setTimeout(() => window.print(), 500);
            }}
            className={`flex items-center gap-2 px-4 py-2 border rounded-lg text-sm font-bold transition-all ${isDark ? "bg-black/20 border-white/10 text-gray-300 hover:bg-white/10" : "bg-white border-gray-200 text-gray-600 hover:bg-gray-50"}`}
          >
            <FileText className="w-4 h-4" /> PDF
          </button>
        </div>
      </div>

      {/* ── KPI Row 1 ── */}
      {errors.kpi && <ErrBanner msg={errors.kpi} />}
      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4">
        {[
          {
            label: "Total assigned leads",
            value: fmtN(totalLeads > 0 ? totalLeads : (kpi?.totalAssignedLeads ?? kpi?.totalLeads ?? (ordersPlaced > 0 ? ordersPlaced : 0))),
            trend: kpi?.leadsTrend,
            sub: `${startDate} – ${endDate}`,
          },
          {
            label: "Orders placed",
            value: fmtN(ordersPlaced),
            trend: kpi?.ordersTrend,
            sub:
              grossConvPct != null
                ? `${fmtPct(grossConvPct)} gross conversion`
                : null,
          },
          {
            label: "Verified orders",
            value: fmtN(verifiedOrders),
            trend: kpi?.verifiedTrend,
            sub:
              verifiedConvPct != null
                ? `${fmtPct(verifiedConvPct)} verified conversion`
                : verificationPct != null
                  ? `${fmtPct(verificationPct)} verification rate`
                  : null,
            subColor: "text-green-600",
          },
          {
            label: "Avg. order value",
            value: fmtRs(kpi?.avgOrderValue ?? kpi?.aov),
            trend: kpi?.aovTrend,
            sub: "Average",
            subColor: "text-orange-500",
          },
          {
            label: "Delivered orders",
            value: fmtN(deliveredOrders),
            trend: kpi?.deliveryTrend,
            sub:
              deliveryPct != null
                ? `${fmtPct(deliveryPct)} delivery rate`
                : null,
            subColor: "text-green-600",
          },
        ].map((card, i) => (
          <div
            key={i}
            className={`p-5 rounded-2xl border shadow-sm hover:shadow-md transition-shadow group ${isDark ? "bg-black/20 border-white/10 text-white" : "bg-white border-gray-100"}`}
          >
            <div className="flex justify-between items-start mb-2">
              <span className="text-sm font-bold text-gray-500 max-w-[120px]">
                {card.label}
              </span>
              {card.trend != null && (
                <div className="flex items-center gap-1 font-bold text-xs text-green-500">
                  <TrendingUp className="w-3 h-3" />
                  {typeof card.trend === "number"
                    ? fmtPct(card.trend)
                    : card.trend}
                </div>
              )}
            </div>
            <div className="text-3xl font-black text-gray-800 mb-1">
              {loading.kpi ? <Spinner /> : (card.value ?? "—")}
            </div>
            {card.sub && (
              <p className="text-xs font-medium">
                <span className={card.subColor ?? "text-gray-400"}>
                  {card.sub}
                </span>
              </p>
            )}
          </div>
        ))}
      </div>

      {/* ── KPI Row 2 ── */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
        {[
          {
            label: "Gross conversion %",
            value: fmtPct(grossConvPct),
            raw: grossConvPct ?? 0,
            color: currentTheme.color,
          },
          {
            label: "Verified conversion %",
            value: fmtPct(verifiedConvPct),
            raw: verifiedConvPct ?? 0,
            sub:
              verificationPct != null
                ? `${fmtPct(verificationPct)} verification rate`
                : null,
            subColor: "text-green-600",
            color: currentTheme.color,
          },
          {
            label: "Net conversion %",
            value: fmtPct(netConvPct),
            raw: netConvPct ?? 0,
            color: currentTheme.color,
          },
          {
            label: "Delivery %",
            value: fmtPct(deliveryPct),
            raw: deliveryPct ?? 0,
            sub:
              kpi?.deliveryTarget != null
                ? `Target: ${fmtPct(kpi.deliveryTarget)}`
                : null,
            subColor: "text-green-600",
            color: currentTheme.color,
          },
          {
            label: "Avg AHT",
            value: fmtTime(avgAHT),
            raw: avgAHT ?? 0,
            sub:
              avgAHT != null && !isNaN(Number(avgAHT)) && Number(avgAHT) > 0
                ? `${Math.round(Number(avgAHT))}s avg`
                : null,
            color: "#94a3b8",
          },
        ].map((card, i) => (
          <div
            key={i}
            className={`p-5 rounded-2xl border shadow-sm ${isDark ? "bg-black/20 border-white/10 text-white" : "bg-white border-gray-100"}`}
          >
            <span className="text-xs font-bold text-gray-400 uppercase tracking-wider">
              {card.label}
            </span>
            <div className="text-2xl font-black text-gray-800 my-1">
              {loading.kpi ? <Spinner /> : (card.value ?? "—")}
            </div>
            {card.sub && (
              <p
                className={`text-[10px] font-bold ${card.subColor ?? "text-gray-400"}`}
              >
                {card.sub}
              </p>
            )}
            <div className="w-full h-1.5 bg-gray-100 rounded-full mt-3 overflow-hidden">
              <div
                className="h-full rounded-full transition-all duration-700"
                style={{
                  width: `${Math.min(100, Math.max(0, card.raw ?? 0))}%`,
                  backgroundColor: card.color,
                }}
              />
            </div>
          </div>
        ))}
      </div>

      {/* ── Monthly Performance + Funnel ── */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Achievement card */}
        <div
          className={`lg:col-span-2 p-6 rounded-3xl border shadow-sm flex flex-col md:flex-row items-center gap-8 ${isDark ? "bg-black/20 border-white/10 text-white" : "bg-white border-gray-100"}`}
        >
          <div className="flex-1">
            <div
              className="flex items-center gap-2 text-xs font-bold uppercase mb-4 tracking-widest"
              style={{ color: currentTheme.color }}
            >
              <div
                className="w-5 h-5 rounded flex items-center justify-center"
                style={{ backgroundColor: `${currentTheme.color}20` }}
              >
                <div
                  className="w-2 h-2 rounded-sm"
                  style={{ backgroundColor: currentTheme.color }}
                />
              </div>
              Monthly business performance
            </div>
            {loading.monthly ? (
              <div className="py-8 text-center text-gray-400 text-sm">
                <Spinner />
                Loading…
              </div>
            ) : errors.monthly ? (
              <ErrBanner msg={errors.monthly} />
            ) : (
              <>
                <div className="flex items-baseline gap-2 mb-6">
                  <h2 className="text-6xl font-black text-gray-900 tracking-tighter">
                    {fmtN(achieved)}
                  </h2>
                  <span className="text-2xl font-bold text-gray-400">
                    / {fmtN(target)}{" "}
                    {activeTab === "REVENUE"
                      ? "₹"
                      : activeTab === "CONVERSION"
                        ? "%"
                        : "orders"}
                  </span>
                </div>
                <div className="flex flex-wrap items-center gap-4">
                  {gap > 0 && (
                    <div
                      className={`flex items-center gap-2 px-3 py-2 rounded-full text-xs font-bold border ${isDark ? "bg-black/40 text-orange-400 border-orange-500/20" : "bg-orange-50 text-orange-600 border-orange-100"}`}
                    >
                      <AlertCircle className="w-4 h-4" />
                      {fmtN(gap)} shortfall
                    </div>
                  )}
                  {(monthly?.daysRemaining ?? kpi?.daysRemaining) != null && (
                    <p className="text-xs text-gray-400 font-bold">
                      Goal deadline:{" "}
                      {monthly?.daysRemaining ?? kpi?.daysRemaining} days
                      remaining
                    </p>
                  )}
                </div>
              </>
            )}
          </div>

          {/* Gauge */}
          <div className="relative flex flex-col items-center flex-shrink-0">
            <div className="relative w-48 h-28 overflow-hidden">
              <svg width="192" height="96" viewBox="0 0 192 96">
                <path
                  d="M16 88 A80 80 0 0 1 176 88"
                  stroke="#f3f4f6"
                  strokeWidth="18"
                  fill="none"
                  strokeLinecap="round"
                />
                <path
                  d="M16 88 A80 80 0 0 1 176 88"
                  stroke={currentTheme.color}
                  strokeWidth="18"
                  fill="none"
                  strokeLinecap="round"
                  strokeDasharray="251"
                  strokeDashoffset={251 - (251 * Math.min(100, pct)) / 100}
                  style={{ transition: "stroke-dashoffset 1s ease" }}
                />
              </svg>
              <div className="absolute bottom-0 left-1/2 -translate-x-1/2 text-center">
                <span className="text-4xl font-black text-gray-800">
                  {pct}%
                </span>
                <p
                  className="text-[10px] font-black uppercase tracking-widest"
                  style={{ color: currentTheme.color }}
                >
                  Achievement
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Funnel */}
        <div
          className={`p-6 rounded-3xl border shadow-sm min-h-[320px] ${isDark ? "bg-black/20 border-white/10 text-white" : "bg-white border-gray-100"}`}
        >
          <h3
            className={`text-sm font-bold mb-6 ${isDark ? "text-gray-200" : "text-gray-700"}`}
          >
            {dates?.[0]?.format("MMMM")} sales funnel
          </h3>
          {loading.funnel ? (
            <div className="flex items-center justify-center h-40 text-gray-400 text-sm">
              <Spinner />
              Loading…
            </div>
          ) : errors.funnel ? (
            <ErrBanner msg={errors.funnel} />
          ) : funnelSteps.length === 0 ? (
            <p className="text-sm text-gray-400 text-center mt-8">
              No funnel data
            </p>
          ) : (
            <>
              <div className="flex flex-col items-center gap-1">
                {funnelSteps.map((step, i) => {
                  const topW = 280 - i * 44;
                  const botW = 280 - (i + 1) * 44;
                  const svgW = 300;
                  const tX = (svgW - topW) / 2;
                  const bX = (svgW - botW) / 2;
                  const pctLbl =
                    funnelSteps[0].value > 0
                      ? `${((step.value / funnelSteps[0].value) * 100).toFixed(1)}%`
                      : "0%";
                  const path = `M ${tX} 0 L ${tX + topW} 0 Q ${tX + topW - 6} 22 ${bX + botW} 44 L ${bX} 44 Q ${tX + 6} 22 ${tX} 0 Z`;
                  return (
                    <div
                      key={step.label}
                      className="relative"
                      style={{ width: svgW, height: 44 }}
                    >
                      <svg
                        width={svgW}
                        height={44}
                        viewBox={`0 0 ${svgW} 44`}
                        style={{ display: "block" }}
                      >
                        <path d={path} fill={step.color} />
                      </svg>
                      <span className="absolute inset-0 flex items-center justify-center text-white text-[11px] font-black tracking-wide pointer-events-none">
                        {step.label}: {fmtN(step.value)} ({pctLbl})
                      </span>
                    </div>
                  );
                })}
              </div>
              <div className="mt-8 pt-5 border-t border-gray-50 flex justify-around">
                <div className="text-center">
                  <p className="text-[10px] font-extrabold text-gray-400 uppercase mb-1 tracking-widest">
                    Conversion
                  </p>
                  <p className="text-lg font-black text-gray-800">
                    {fmtPct(funnel?.conversionRate ?? funnel?.conversion ?? grossConvPct)}
                  </p>
                </div>
                <div className="text-center">
                  <p className="text-[10px] font-extrabold text-gray-400 uppercase mb-1 tracking-widest">
                    Verified Conv.
                  </p>
                  <p className="text-lg font-black text-gray-800">
                    {fmtPct(verifiedConvPct)}
                  </p>
                </div>
                <div className="text-center">
                  <p className="text-[10px] font-extrabold text-gray-400 uppercase mb-1 tracking-widest">
                    Efficiency
                  </p>
                  <p
                    className="text-lg font-black"
                    style={{ color: currentTheme.color }}
                  >
                    {fmtPct(funnel?.efficiency ?? funnel?.efficiencyRate ?? verificationPct)}
                  </p>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* ── Managers Focus ── */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-black text-gray-800">
            Managers focus details
          </h2>
          <button
            onClick={handleViewAllManagers}
            className="flex items-center text-sm font-bold hover:opacity-80"
            style={{ color: currentTheme.color }}
          >
            View all managers <ChevronRight className="w-4 h-4 ml-1" />
          </button>
        </div>
        {errors.managers && <ErrBanner msg={errors.managers} />}
        {loading.managers ? (
          <div className="text-center py-10 text-gray-400">
            <Spinner />
            Loading managers…
          </div>
        ) : managers.length === 0 ? (
          <p className="text-sm text-gray-400 text-center py-8">
            No manager data available.
          </p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {managers
              .filter((m) => {
                if (!searchQuery) return true;
                const q = searchQuery.toLowerCase();
                return (
                  m.name?.toLowerCase().includes(q) ||
                  String(m.managerId ?? m.id ?? "").includes(q)
                );
              })
              .map((manager) => {
                const mId = manager.id ?? manager.managerId;
                const mPct =
                  manager.target > 0
                    ? Math.round((manager.achieved / manager.target) * 100)
                    : 0;
                const sc = statusConfig(mPct);
                return (
                  <div
                    key={mId}
                    className={`p-6 rounded-3xl border shadow-sm relative overflow-hidden group hover:shadow-lg transition-all ${isDark ? "bg-black/20 border-white/10 text-white" : "bg-white"} ${selectedMgr === mId ? (isDark ? "border-blue-500/50" : "border-blue-200") : isDark ? "border-white/10" : "border-gray-100"}`}
                  >
                    <div className="flex items-center gap-4 mb-6">
                      <div className="w-14 h-14 bg-gray-200 rounded-full border-2 border-white shadow-sm overflow-hidden flex-shrink-0">
                        <img
                          src={`https://api.dicebear.com/7.x/avataaars/svg?seed=${manager.managerName}`}
                          alt={manager.managerName}
                        />
                      </div>
                      <div>
                        <h4 className="text-base font-black text-gray-900 leading-none mb-1">
                          {manager.managerName}
                        </h4>
                        <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">
                          {manager.role ?? manager.designation ?? "Manager"}
                        </p>
                      </div>
                    </div>
                    <div className="flex justify-between mb-6">
                      <div>
                        <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1">
                          Target
                        </p>
                        <p className="text-xl font-black text-gray-800">
                          {fmtN(manager.target)}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1">
                          Achieved
                        </p>
                        <p className="text-xl font-black text-gray-800">
                          {fmtN(manager.achieved)}
                        </p>
                      </div>
                    </div>
                    <div className="mb-6">
                      <div className="flex justify-between items-end mb-2">
                        <span
                          className="text-[12px] font-black"
                          style={{ color: sc.color }}
                        >
                          {mPct}% achievement
                        </span>
                        <span className="text-[10px] font-bold text-gray-400">
                          {sc.label}
                        </span>
                      </div>
                      <div className="w-full h-2.5 bg-gray-100 rounded-full overflow-hidden">
                        <div
                          className="h-full rounded-full transition-all duration-700"
                          style={{
                            width: `${mPct}%`,
                            backgroundColor: sc.color,
                          }}
                        />
                      </div>
                    </div>
                    <div className="pt-4 border-t border-gray-50 flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div
                          className={`w-8 h-8 rounded-lg flex items-center justify-center ${isDark ? "bg-orange-500/20" : "bg-orange-50"}`}
                        >
                          <Users
                            className={`w-4 h-4 ${isDark ? "text-orange-400" : "text-orange-500"}`}
                          />
                        </div>
                        <div>
                          <p className="text-xs font-black text-gray-800 leading-none">
                            View team insights
                          </p>
                          <p className="text-[10px] text-gray-400 mt-1 font-medium">
                            {manager.teamLeadCount ?? manager.leads ?? "—"} TLs
                            • {manager.agentCount ?? manager.agents ?? "—"}{" "}
                            agents
                          </p>
                        </div>
                      </div>
                      <button
                        onClick={() => loadTeamInsights(mId)}
                        className={`w-8 h-8 rounded-full flex items-center justify-center transition-colors ${isDark ? "bg-white/10 hover:bg-white/20" : "bg-gray-50 hover:bg-gray-100"}`}
                      >
                        {loading.insights && selectedMgr === mId ? (
                          <Loader2
                            className="w-4 h-4 animate-spin"
                            style={{ color: currentTheme.color }}
                          />
                        ) : (
                          <ChevronDown className="w-4 h-4 text-gray-400" />
                        )}
                      </button>
                    </div>
                  </div>
                );
              })}
          </div>
        )}
      </div>

      {/* ── Team Insights ── */}
      {(teamInsights || loading.insights) && (
        <div
          className={`p-6 rounded-[32px] border shadow-sm relative overflow-hidden ${isDark ? "bg-black/20 border-white/10 text-white" : "bg-white border-orange-100"}`}
        >
          <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-orange-50 via-orange-100 to-orange-50" />
          <div
            className={`flex flex-wrap items-center justify-between gap-4 mb-8 p-5 rounded-2xl border ${isDark ? "bg-black/40 border-white/5" : "bg-[#F8FAFC] border-gray-50"}`}
          >
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center shadow-sm">
                <Users className="w-6 h-6 text-orange-500" />
              </div>
              <div>
                <h2 className="text-lg font-black text-gray-800">
                  {teamInsights?.managerName
                    ? `Team insights: ${teamInsights.managerName}'s group`
                    : "Team insights"}
                </h2>
                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mt-1">
                  {teamInsights?.teamLeadCount ?? "—"} team leads •{" "}
                  {teamInsights?.agentCount ?? "—"} agents
                </p>
              </div>
            </div>
            <button
              onClick={() => setIsAssignModalOpen(true)}
              className={`border px-5 py-2.5 rounded-xl text-xs font-black shadow-sm transition-all active:scale-95 ${isDark ? "bg-black/20 border-white/10 text-gray-300 hover:bg-white/10" : "bg-white border-gray-200 hover:bg-gray-50 text-gray-700"}`}
            >
              Manage assignment
            </button>
          </div>

          {errors.insights && <ErrBanner msg={errors.insights} />}

          {loading.insights ? (
            <div className="text-center py-8 text-gray-400">
              <Spinner />
              Loading team insights…
            </div>
          ) : (
            <div className="space-y-4">
              {(teamInsights?.teamLeads ?? teamInsights?.teamLead ?? []).map(
                (lead) => {
                  const tlId = lead.id ?? lead.employeeId;
                  const tlPct =
                    lead.target > 0
                      ? Math.round((lead.achieved / lead.target) * 100)
                      : 0;
                  const sc = statusConfig(tlPct);
                  const isExp = expandedLead === tlId;
                  const agents = agentsByLead[tlId] ?? lead.agents ?? [];
                  const tlGap = lead.gap ?? lead.target - lead.achieved;

                  return (
                    <div
                      key={tlId}
                      className={`rounded-2xl border overflow-hidden shadow-sm transition-all duration-300 ${isDark ? "bg-black/20 border-white/10 text-white" : "bg-white border-gray-100"}`}
                    >
                      <div
                        onClick={() => toggleLeadExpansion(tlId)}
                        className={`flex flex-wrap items-center justify-between gap-4 p-4 cursor-pointer transition-all group ${isDark ? "hover:bg-white/5" : "hover:bg-gray-50/50"} ${isExp ? (isDark ? "bg-white/10" : "bg-[#F8FAFC]") : ""}`}
                      >
                        <div className="flex items-center gap-6 min-w-[280px]">
                          <ChevronRight
                            className={`w-4 h-4 text-gray-300 transition-transform duration-300 ${isExp ? "rotate-90" : ""}`}
                            style={isExp ? { color: currentTheme.color } : {}}
                          />
                          <div className="flex items-center gap-4">
                            <div className="w-10 h-10 rounded-full bg-gray-200 border-2 border-white shadow-sm overflow-hidden flex-shrink-0">
                              <img
                                src={`https://api.dicebear.com/7.x/avataaars/svg?seed=${lead.name}`}
                                alt={lead.name}
                              />
                            </div>
                            <div>
                              <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest leading-none mb-1">
                                Team lead
                              </p>
                              <p className="text-sm font-black text-gray-800">
                                {lead.name}
                              </p>
                            </div>
                          </div>
                        </div>

                        <div className="grid grid-cols-4 gap-12 flex-1 max-w-2xl px-6">
                          {[
                            {
                              lbl: "Target",
                              val: fmtN(lead.target),
                              color: "text-gray-800",
                            },
                            {
                              lbl: "Achieved",
                              val: fmtN(lead.achieved),
                              color: "text-gray-800",
                            },
                            {
                              lbl: "Avg. order value",
                              val: fmtRs(lead.avgOrderValue ?? lead.aov),
                              color: "text-orange-500",
                            },
                            {
                              lbl: "Gap",
                              val: fmtN(tlGap),
                              color:
                                tlGap < 0 ? "text-red-500" : "text-green-500",
                            },
                          ].map((s) => (
                            <div key={s.lbl}>
                              <p className="text-[9px] font-bold text-gray-400 uppercase tracking-widest mb-1">
                                {s.lbl}
                              </p>
                              <p className={`text-base font-black ${s.color}`}>
                                {s.val}
                              </p>
                            </div>
                          ))}
                        </div>

                        <div className="min-w-[150px] text-right">
                          <span
                            className="px-4 py-2 rounded-xl text-[9px] font-black border tracking-wider"
                            style={{
                              color: sc.color,
                              backgroundColor: sc.bg,
                              borderColor: sc.border,
                            }}
                          >
                            {sc.label.toUpperCase()} ({tlPct}%)
                          </span>
                        </div>
                      </div>

                      {/* Agents sub-table */}
                      {isExp && (
                        <div className="bg-[#FBFCFE] border-t border-gray-100 p-6 animate-in slide-in-from-top-2 duration-300">
                          <div className="grid grid-cols-7 gap-4 px-6 mb-4 text-[10px] font-bold text-gray-400 uppercase tracking-widest">
                            <div className="col-span-2 ml-10">Agent name</div>
                            <div>Target</div>
                            <div>Orders</div>
                            <div>Avg. OV</div>
                            <div>Conv %</div>
                            <div>Delivery %</div>
                          </div>
                          {agents.length === 0 ? (
                            <p className="text-xs text-gray-400 text-center py-4">
                              No agent data
                            </p>
                          ) : (
                            <div className="space-y-2">
                              {agents.map((agent, j) => {
                                const conv =
                                  agent.conversionRate ?? agent.conversion ?? 0;
                                const del =
                                  agent.deliveryRate ?? agent.delivery ?? 0;
                                const aColor =
                                  conv >= 15
                                    ? currentTheme.color
                                    : conv >= 10
                                      ? "#3b82f6"
                                      : "#ef4444";
                                const dBg =
                                  del >= 90
                                    ? `${currentTheme.color}10`
                                    : "#fff7ed";
                                const dClr =
                                  del >= 90 ? currentTheme.color : "#f97316";
                                return (
                                  <div
                                    key={j}
                                    className="flex items-center h-14 bg-white rounded-xl border border-gray-50 shadow-sm relative overflow-hidden"
                                    style={{
                                      borderLeft: `3px solid ${aColor}`,
                                    }}
                                  >
                                    <div className="grid grid-cols-7 gap-4 flex-1 items-center px-6">
                                      <div className="col-span-2 flex items-center gap-3 ml-4">
                                        <div className="w-8 h-8 rounded-full bg-gray-100 overflow-hidden flex-shrink-0">
                                          <img
                                            src={`https://api.dicebear.com/7.x/avataaars/svg?seed=${agent.name}`}
                                            alt={agent.name}
                                          />
                                        </div>
                                        <span className="text-xs font-black text-gray-700 truncate">
                                          {agent.name}
                                        </span>
                                      </div>
                                      <div className="text-xs font-bold text-gray-500">
                                        {fmtN(agent.target)}
                                      </div>
                                      <div className="text-xs font-black text-gray-800">
                                        {fmtN(
                                          agent.ordersPlaced ?? agent.orders,
                                        )}
                                      </div>
                                      <div className="text-xs font-black text-orange-500">
                                        {fmtRs(
                                          agent.avgOrderValue ?? agent.aov,
                                        )}
                                      </div>
                                      <div className="flex items-center gap-3">
                                        <span
                                          className="text-[10px] font-black"
                                          style={{ color: aColor }}
                                        >
                                          {fmtPct(conv)}
                                        </span>
                                        <div className="w-16 h-1 bg-gray-100 rounded-full overflow-hidden">
                                          <div
                                            className="h-full rounded-full"
                                            style={{
                                              width: `${Math.min(100, conv)}%`,
                                              backgroundColor: aColor,
                                            }}
                                          />
                                        </div>
                                      </div>
                                      <div>
                                        <span
                                          className="px-2 py-1 rounded-md text-[9px] font-black"
                                          style={{
                                            color: dClr,
                                            backgroundColor: dBg,
                                          }}
                                        >
                                          {fmtPct(del)}
                                        </span>
                                      </div>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                          <div className="mt-4 flex items-center justify-between px-6 border-t border-gray-50 pt-4">
                            <p className="text-xs text-gray-400 font-bold">
                              Showing {agents.length} agents
                            </p>
                            <button
                              onClick={() =>
                                navigate(
                                  `/mis-reports/teamleads/${tlId}/agents`,
                                )
                              }
                              className="flex items-center gap-1 text-xs font-black text-blue-600 hover:text-blue-700"
                            >
                              View all agent list{" "}
                              <ChevronRight className="w-4 h-4 mt-0.5" />
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                },
              )}
            </div>
          )}
        </div>
      )}

      {/* ── Target Assignment Modal ── */}
      {isAssignModalOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-6 bg-gray-950/60 backdrop-blur-md animate-in fade-in duration-500">
          <div className="bg-white w-full max-w-lg rounded-[40px] shadow-[0_32px_64px_-12px_rgba(0,0,0,0.14)] border border-white/20 overflow-hidden animate-in zoom-in-95 duration-500">
            <div className="p-10">
              <div className="flex items-start justify-between mb-10">
                <div className="flex items-start gap-4">
                  <div className="w-14 h-14 bg-blue-50 rounded-2xl flex items-center justify-center text-blue-600 shadow-inner">
                    <TrendingUpIcon className="w-7 h-7" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-black text-gray-900 tracking-tight">
                      Assign new target
                    </h3>
                    <p className="text-xs font-bold text-gray-400 uppercase tracking-[0.2em] mt-1.5 opacity-80">
                      Monthly performance goals
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setIsAssignModalOpen(false)}
                  className={`w-12 h-12 flex items-center justify-center rounded-2xl transition-all ${isDark ? "bg-white/10 text-gray-400 hover:bg-red-500/20 hover:text-red-400" : "bg-gray-50 text-gray-300 hover:bg-red-50 hover:text-red-500"}`}
                >
                  <AlertCircle className="w-6 h-6 rotate-45" />
                </button>
              </div>

              <div className="space-y-8">
                {/* Level toggle */}
                <div>
                  <div className="flex items-center gap-2 mb-4">
                    <Users className="w-4 h-4 text-blue-500" />
                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">
                      Select assignment level
                    </label>
                  </div>
                  <div
                    className={`flex p-1.5 rounded-[22px] border ${isDark ? "bg-black/40 border-white/5" : "bg-gray-50 border-gray-100/50"}`}
                  >
                    {[
                      { label: "Team lead", val: "TEAM_LEAD" },
                      { label: "Individual agent", val: "EMPLOYEE" },
                    ].map((opt) => (
                      <button
                        key={opt.val}
                        onClick={() =>
                          setAssignment((a) => ({
                            ...a,
                            type: opt.val,
                            id: "",
                          }))
                        }
                        className={`flex-1 py-3.5 px-6 rounded-[18px] text-xs font-black transition-all duration-300 ${
                          assignment.type === opt.val
                            ? "bg-white text-blue-600 shadow-[0_8px_16px_-4px_rgba(59,130,246,0.15)] border border-blue-100/50"
                            : "text-gray-400 hover:text-gray-600"
                        }`}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Assignee — live from /targets/assignees */}
                <div>
                  <div className="flex items-center gap-2 mb-4">
                    <Search className="w-4 h-4 text-orange-500" />
                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">
                      Select{" "}
                      {assignment.type === "TEAM_LEAD" ? "team lead" : "agent"}
                    </label>
                  </div>
                  <div className="relative">
                    <select
                      value={assignment.id}
                      onChange={(e) =>
                        setAssignment((a) => ({ ...a, id: e.target.value }))
                      }
                      className={`w-full border rounded-2xl px-6 py-4 text-sm font-bold focus:outline-none focus:ring-4 transition-all appearance-none cursor-pointer ${isDark ? "bg-black/40 border-white/10 text-white focus:ring-blue-500/20 focus:bg-black/60" : "bg-gray-50 border-gray-100 text-gray-700 focus:ring-blue-500/10 focus:bg-white"}`}
                    >
                      <option value="">Select a member…</option>
                      {assignees.map((a) => (
                        <option
                          key={a.id ?? a.employeeId}
                          value={a.id ?? a.employeeId}
                        >
                          {a.name}
                          {a.teamName ? ` — ${a.teamName}` : ""}
                        </option>
                      ))}
                    </select>
                    <ChevronDown className="absolute right-6 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-300 pointer-events-none" />
                  </div>
                </div>

                {/* Target value */}
                <div>
                  <div className="flex items-center gap-2 mb-4">
                    <TrendingUpIcon className="w-4 h-4 text-green-500" />
                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">
                      Monthly target orders
                    </label>
                  </div>
                  <div className="relative group">
                    <input
                      type="number"
                      placeholder="e.g. 1500"
                      value={assignment.target}
                      onChange={(e) =>
                        setAssignment((a) => ({ ...a, target: e.target.value }))
                      }
                      className={`w-full border rounded-2xl px-6 py-4 text-base font-black placeholder:text-gray-300 focus:outline-none focus:ring-4 transition-all pr-16 ${isDark ? "bg-black/40 border-white/10 text-white focus:ring-blue-500/20 focus:bg-black/60" : "bg-gray-50 border-gray-100 text-gray-800 focus:ring-blue-500/10 focus:bg-white"}`}
                    />
                    <div className="absolute right-6 top-1/2 -translate-y-1/2 flex items-center gap-1.5 opacity-40 group-focus-within:opacity-100 transition-opacity">
                      <span className="text-[10px] font-black text-blue-600">
                        UNITS
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-12 flex items-center gap-4">
                <button
                  onClick={() => setIsAssignModalOpen(false)}
                  className={`flex-1 py-4 px-6 rounded-2xl text-xs font-black transition-all tracking-wider ${isDark ? "text-gray-500 hover:text-gray-300 hover:bg-white/5" : "text-gray-400 hover:text-gray-600 hover:bg-gray-50"}`}
                >
                  DISCARD
                </button>
                <button
                  onClick={submitAssignment}
                  disabled={assigning}
                  className="flex-[2] bg-gradient-to-br from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 disabled:opacity-60 text-white px-10 py-5 rounded-3xl text-sm font-black shadow-[0_20px_40px_-12px_rgba(37,99,235,0.4)] transition-all active:scale-95 flex items-center justify-center gap-3 group"
                >
                  {assigning && <Loader2 className="w-4 h-4 animate-spin" />}
                  APPLY NEW TARGET
                  {!assigning && (
                    <ChevronRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Toast ── */}
      {toast.show && (
        <div className="fixed top-10 left-1/2 -translate-x-1/2 z-[200] animate-in slide-in-from-top-10 fade-in duration-500">
          <div
            className={`px-8 py-4 rounded-[24px] shadow-2xl flex items-center gap-4 ${toast.isError ? "bg-red-600" : "bg-gray-900/90 backdrop-blur-xl border border-white/10"}`}
          >
            <div
              className={`w-8 h-8 rounded-full flex items-center justify-center ${toast.isError ? "bg-red-400" : "bg-green-500"} shadow-lg`}
            >
              {toast.isError ? (
                <AlertCircle className="w-4 h-4 text-white" />
              ) : (
                <TrendingUpIcon className="w-4 h-4 text-white" />
              )}
            </div>
            <p className="text-sm font-black text-white tracking-wide">
              {toast.message}
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default BusinessDashboard;
